import React, { useState } from "react";
import {
	alpha,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Paper,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongTwoToneIcon from "@mui/icons-material/ReceiptLongTwoTone";
import ContentCopyTwoToneIcon from "@mui/icons-material/ContentCopyTwoTone";
import CheckTwoToneIcon from "@mui/icons-material/CheckTwoTone";
import moment from "moment";
import type { ITicketListItem } from "../type/ticketScanType";

interface Props {
	open: boolean;
	handleClose: () => void;
	ticket: ITicketListItem | null;
}

const keyLabelMap: Record<string, string> = {
	trxID: "Transaction ID",
	trx_id: "Transaction ID",
	paymentID: "Payment ID",
	payment_id: "Payment ID",
	payerAccount: "Payer Account",
	payer_account: "Payer Account",
	customerMsisdn: "Customer MSISDN",
	customer_msisdn: "Customer MSISDN",
	paymentExecuteTime: "Payment Execute Time",
	payment_execute_time: "Payment Execute Time",
	merchantInvoiceNumber: "Merchant Invoice No",
	amount: "Amount",
	currency: "Currency",
	intent: "Intent",
	note: "Note",
	statusMessage: "Status Message",
	transactionStatus: "Transaction Status",
	verificationStatus: "Verification Status",
};

const formatKey = (key: string): string => {
	if (keyLabelMap[key]) return keyLabelMap[key];

	return key
		.replace(/([A-Z])/g, " $1")
		.replace(/_/g, " ")
		.replace(/^./, (str) => str.toUpperCase())
		.trim();
};

const PaymentDetailsModal: React.FC<Props> = ({
	open,
	handleClose,
	ticket,
}) => {
	const theme = useTheme();
	const [copiedKey, setCopiedKey] = useState<string | null>(null);

	if (!ticket) return null;

	let details: Record<string, any> = {};
	if (ticket.payment_details) {
		if (typeof ticket.payment_details === "string") {
			try {
				details = JSON.parse(ticket.payment_details);
			} catch {
				details = { raw: ticket.payment_details };
			}
		} else if (typeof ticket.payment_details === "object") {
			details = ticket.payment_details;
		}
	}

	const entries = Object.entries(details).filter(
		([key, val]) => val !== undefined && val !== null && val !== ""
	);

	const handleCopy = (key: string, text: string) => {
		navigator.clipboard.writeText(text);
		setCopiedKey(key);
		setTimeout(() => {
			setCopiedKey(null);
		}, 2000);
	};

	const formatValue = (key: string, value: any): React.ReactNode => {
		if (typeof value === "object") {
			return JSON.stringify(value);
		}

		const str = String(value);

		// Format date if matches execute time or date patterns
		if (
			(key.toLowerCase().includes("time") || key.toLowerCase().includes("date")) &&
			isNaN(Number(str)) &&
			moment(str).isValid()
		) {
			return moment(str).format("lll");
		}

		// Format amount
		if (key.toLowerCase() === "amount" && !isNaN(Number(str))) {
			return `৳${str}`;
		}

		return str;
	};

	const isCopyable = (key: string) => {
		const lower = key.toLowerCase();
		return (
			lower.includes("trx") ||
			lower.includes("paymentid") ||
			lower.includes("account") ||
			lower.includes("msisdn") ||
			lower.includes("invoice")
		);
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			disableEnforceFocus
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<ReceiptLongTwoToneIcon color="primary" />
					<Typography variant="h4" fontWeight={600}>
						Payment Details
					</Typography>
				</Box>
				<IconButton onClick={handleClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ pt: 2, pb: 2 }}>
				{/* Top Summary Banner */}
				<Paper
					variant="outlined"
					sx={{
						p: 1.5,
						mb: 2,
						borderRadius: 2,
						bgcolor: alpha(theme.palette.primary.main, 0.04),
						borderColor: alpha(theme.palette.primary.main, 0.15),
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 1.5,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Typography variant="caption" color="text.secondary" fontWeight={600}>
							Ticket No:
						</Typography>
						<Chip
							label={ticket.ticket_no || `#${ticket.id}`}
							size="small"
							color="primary"
							variant="outlined"
							sx={{ fontWeight: 700 }}
						/>
					</Box>

					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						{ticket.payment_method && (
							<Chip
								label={ticket.payment_method.toUpperCase()}
								size="small"
								variant="filled"
								sx={{
									fontWeight: 700,
									fontSize: "0.7rem",
									bgcolor: alpha(theme.palette.primary.main, 0.12),
									color: "primary.main",
								}}
							/>
						)}
						<Chip
							label={(ticket.payment_status || "Paid").toUpperCase()}
							size="small"
							color={
								(ticket.payment_status || "").toLowerCase() === "paid"
									? "success"
									: (ticket.payment_status || "").toLowerCase().includes("pending")
									? "warning"
									: "error"
							}
							sx={{ fontWeight: 700, fontSize: "0.7rem" }}
						/>
					</Box>
				</Paper>

				{/* Key & Value List (Centered and meeting closely in the middle) */}
				{entries.length === 0 ? (
					<Box sx={{ py: 3, textAlign: "center" }}>
						<Typography variant="body2" color="text.secondary">
							No additional payment details available.
						</Typography>
					</Box>
				) : (
					<Paper
						variant="outlined"
						sx={{
							borderRadius: 2,
							p: 1.5,
							bgcolor: "background.paper",
						}}
					>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
							{entries.map(([key, val], idx) => {
								const formattedVal = formatValue(key, val);
								const canCopy = isCopyable(key) && typeof val !== "object";
								const isCopied = copiedKey === key;

								return (
									<Box
										key={key}
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											py: 0.75,
											px: 1.2,
											borderRadius: 1.5,
											bgcolor: idx % 2 === 0 ? alpha(theme.palette.grey[500], 0.04) : "transparent",
											transition: "background-color 0.15s",
											"&:hover": {
												bgcolor: alpha(theme.palette.primary.main, 0.05),
											},
										}}
									>
										{/* Key (Right aligned to meet middle) */}
										<Box
											sx={{
												flex: "1 1 45%",
												textAlign: "right",
												pr: 1.5,
											}}
										>
											<Typography
												variant="body2"
												fontWeight={600}
												color="text.secondary"
												sx={{ fontSize: "0.825rem" }}
											>
												{formatKey(key)}
											</Typography>
										</Box>

										{/* Middle Divider Colon */}
										<Typography
											variant="body2"
											fontWeight={700}
											color="text.secondary"
											sx={{ opacity: 0.6, userSelect: "none" }}
										>
											:
										</Typography>

										{/* Value (Left aligned to meet middle) */}
										<Box
											sx={{
												flex: "1 1 50%",
												textAlign: "left",
												pl: 1.5,
												display: "flex",
												alignItems: "center",
												gap: 0.6,
												wordBreak: "break-word",
											}}
										>
											<Typography
												variant="body2"
												fontWeight={600}
												color="text.primary"
												sx={{
													fontSize: "0.825rem",
													fontFamily:
														key.toLowerCase().includes("id") ||
														key.toLowerCase().includes("trx") ||
														key.toLowerCase().includes("account")
															? "monospace"
															: "inherit",
												}}
											>
												{formattedVal}
											</Typography>

											{canCopy && (
												<Tooltip
													title={isCopied ? "Copied!" : `Copy ${formatKey(key)}`}
													placement="top"
													arrow
												>
													<IconButton
														size="small"
														onClick={() => handleCopy(key, String(val))}
														sx={{
															p: 0.3,
															color: isCopied ? "success.main" : "text.secondary",
															"&:hover": { color: "primary.main" },
														}}
													>
														{isCopied ? (
															<CheckTwoToneIcon sx={{ fontSize: 14 }} />
														) : (
															<ContentCopyTwoToneIcon sx={{ fontSize: 14 }} />
														)}
													</IconButton>
												</Tooltip>
											)}
										</Box>
									</Box>
								);
							})}
						</Box>
					</Paper>
				)}
			</DialogContent>

			<Divider />

			<DialogActions sx={{ px: 2.5, py: 1.5 }}>
				<Button onClick={handleClose} size="small" variant="outlined" color="inherit">
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default PaymentDetailsModal;
